import {useEffect, useMemo, useState} from 'react'
import {useMutation} from '@apollo/client'
import {Alert, Badge, Button, Form, Modal, Spinner, Table} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {MERGE_DUPLICATE_CONTACTS} from '../../../gql/mutations/contactsMutation'

/** Marcador de dato ausente: es diseno, no texto traducible. */
const EM_DASH = '—'
/** El extractor recorta los espacios de los extremos del defaultMessage (guia i18n 17.22). */
const SPACE = ' '

export type MergeContactCandidate = {
  id: number
  fullName?: string | null
  email?: string | null
  phone?: string | null
  dateBirth?: string | null
  policyCount: number
  created?: string | null
  reasons?: string[]
}

type Props = {
  show: boolean
  groups: MergeContactCandidate[][]
  analyzedCount?: number
  truncated?: boolean
  onHide: () => void
  onMerged: () => void
}

export const ModalMergeContacts = ({
  show,
  groups,
  analyzedCount,
  truncated,
  onHide,
  onMerged,
}: Props) => {
  const [remainingGroups, setRemainingGroups] = useState(groups)
  const [selected, setSelected] = useState<Record<number, number[]>>({})
  const [primary, setPrimary] = useState<Record<number, number>>({})
  const [mergingGroup, setMergingGroup] = useState<number | null>(null)
  const [mergingAll, setMergingAll] = useState(false)
  const [mergeResults, setMergeResults] = useState<string[]>([])
  const [mergeErrors, setMergeErrors] = useState<string[]>([])
  const [mergeContacts] = useMutation(MERGE_DUPLICATE_CONTACTS)
  const intl = useIntl()

  const unableToMerge = intl.formatMessage({
    id: 'contacts.merge.error.unable',
    defaultMessage: 'Unable to merge contacts.',
  })
  const contactLabel = (id: number) =>
    intl.formatMessage({id: 'contacts.merge.contactNumber', defaultMessage: 'Contact #{id}'}, {id})
  const groupLabel = (n: number) =>
    intl.formatMessage({id: 'contacts.merge.groupNumber', defaultMessage: 'Group {n}'}, {n})
  const mergedIntoText = (count: number, name: string) =>
    intl.formatMessage(
      {
        id: 'contacts.merge.result.mergedInto',
        defaultMessage:
          '{count, plural, one {# duplicate contact merged into {name}.} other {# duplicate contacts merged into {name}.}}',
      },
      {count, name}
    )

  const resetGroupSelection = (nextGroups: MergeContactCandidate[][]) => {
    const nextSelected: Record<number, number[]> = {}
    const nextPrimary: Record<number, number> = {}
    nextGroups.forEach((group, index) => {
      nextSelected[index] = group.map((contact) => contact.id)
      if (group[0]) nextPrimary[index] = group[0].id
    })
    setSelected(nextSelected)
    setPrimary(nextPrimary)
  }

  useEffect(() => {
    setRemainingGroups(groups)
    resetGroupSelection(groups)
    setMergeResults([])
    setMergeErrors([])
  }, [groups, show])

  const selectedTotal = useMemo(
    () => Object.values(selected).reduce((total, ids) => total + ids.length, 0),
    [selected]
  )
  const mergeableGroupCount = useMemo(
    () =>
      remainingGroups.filter((_, index) => {
        const ids = selected[index] || []
        return ids.length >= 2 && ids.includes(primary[index])
      }).length,
    [primary, remainingGroups, selected]
  )

  const toggleContact = (groupIndex: number, contactId: number) => {
    setSelected((current) => {
      const ids = current[groupIndex] || []
      const nextIds = ids.includes(contactId)
        ? ids.filter((id) => id !== contactId)
        : [...ids, contactId]
      return {...current, [groupIndex]: nextIds}
    })
  }

  const mergeGroup = async (groupIndex: number) => {
    const ids = selected[groupIndex] || []
    const primaryId = primary[groupIndex]
    if (ids.length < 2 || !ids.includes(primaryId)) {
      toast.error(
        intl.formatMessage({
          id: 'contacts.merge.toast.selectTwo',
          defaultMessage:
            'Select at least two contacts and choose one of them as the primary contact.',
        })
      )
      return
    }

    setMergingGroup(groupIndex)
    try {
      const result = await mergeContacts({
        variables: {primaryId, duplicateIds: ids.filter((id) => id !== primaryId)},
      })
      const payload = result.data?.mergeDuplicateContacts
      if (!payload?.success) throw new Error(payload?.errors?.[0]?.message || unableToMerge)
      toast.success(
        intl.formatMessage(
          {
            id: 'contacts.merge.toast.merged',
            defaultMessage:
              '{count, plural, one {# duplicate contact merged.} other {# duplicate contacts merged.}}',
          },
          {count: payload.mergedCount}
        )
      )
      const keptContact = remainingGroups[groupIndex]?.find((contact) => contact.id === primaryId)
      const nextGroups = remainingGroups.filter((_, index) => index !== groupIndex)
      setRemainingGroups(nextGroups)
      resetGroupSelection(nextGroups)
      setMergeResults((current) => [
        mergedIntoText(payload.mergedCount, keptContact?.fullName || contactLabel(primaryId)),
        ...current,
      ])
      onMerged()
    } catch (error: any) {
      toast.error(error.message || unableToMerge)
    } finally {
      setMergingGroup(null)
    }
  }

  const mergeAllSelectedGroups = async () => {
    const operations = remainingGroups
      .map((group, groupIndex) => ({
        group,
        groupIndex,
        ids: selected[groupIndex] || [],
        primaryId: primary[groupIndex],
      }))
      .filter(({ids, primaryId}) => ids.length >= 2 && ids.includes(primaryId))

    if (!operations.length) {
      toast.error(
        intl.formatMessage({
          id: 'contacts.merge.toast.selectTwoInGroups',
          defaultMessage: 'Select at least two contacts in one or more groups.',
        })
      )
      return
    }

    setMergingAll(true)
    setMergeErrors([])
    const successfulIndexes = new Set<number>()
    const successfulResults: string[] = []
    const failedResults: string[] = []

    for (const operation of operations) {
      const keptContact = operation.group.find((contact) => contact.id === operation.primaryId)
      try {
        const result = await mergeContacts({
          variables: {
            primaryId: operation.primaryId,
            duplicateIds: operation.ids.filter((id) => id !== operation.primaryId),
          },
        })
        const payload = result.data?.mergeDuplicateContacts
        if (!payload?.success) throw new Error(payload?.errors?.[0]?.message || unableToMerge)
        successfulIndexes.add(operation.groupIndex)
        successfulResults.push(
          mergedIntoText(
            payload.mergedCount,
            keptContact?.fullName || contactLabel(operation.primaryId)
          )
        )
      } catch (error: any) {
        failedResults.push(
          intl.formatMessage(
            {id: 'contacts.merge.result.failed', defaultMessage: '{name}: {error}'},
            {
              name: keptContact?.fullName || groupLabel(operation.groupIndex + 1),
              error: error.message || unableToMerge,
            }
          )
        )
      }
    }

    const nextGroups = remainingGroups.filter((_, index) => !successfulIndexes.has(index))
    setRemainingGroups(nextGroups)
    resetGroupSelection(nextGroups)
    setMergeResults((current) => [...successfulResults, ...current])
    setMergeErrors(failedResults)
    setMergingAll(false)

    if (successfulIndexes.size) {
      toast.success(
        intl.formatMessage(
          {
            id: 'contacts.merge.toast.groupsMerged',
            defaultMessage:
              '{count, plural, one {# candidate group merged.} other {# candidate groups merged.}}',
          },
          {count: successfulIndexes.size}
        )
      )
      onMerged()
    }
    if (failedResults.length)
      toast.error(
        intl.formatMessage(
          {
            id: 'contacts.merge.toast.groupsFailed',
            defaultMessage:
              '{count, plural, one {# group could not be merged.} other {# groups could not be merged.}}',
          },
          {count: failedResults.length}
        )
      )
  }

  return (
    <Modal show={show} onHide={onHide} size='xl' backdrop='static' centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage
            id='contacts.merge.title'
            defaultMessage='Review duplicate contacts'
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
        {typeof analyzedCount === 'number' && (
          <Alert variant='info'>
            <FormattedMessage
              id='contacts.merge.analyzed'
              defaultMessage='Analyzed {count} filtered contacts and found {groups, plural, one {# candidate group} other {# candidate groups}}.'
              values={{count: analyzedCount, groups: remainingGroups.length}}
            />
            {truncated && (
              <>
                {SPACE}
                <FormattedMessage
                  id='contacts.merge.truncated'
                  defaultMessage='Only the first 5,000 contacts were analyzed.'
                />
              </>
            )}
          </Alert>
        )}
        {!!remainingGroups.length && (
          <div className='d-flex justify-content-end mb-3'>
            <Button
              onClick={() => void mergeAllSelectedGroups()}
              disabled={mergingAll || mergingGroup !== null || mergeableGroupCount === 0}
            >
              {mergingAll && <Spinner animation='border' size='sm' className='me-2' />}
              <FormattedMessage
                id='contacts.merge.mergeAll'
                defaultMessage='Merge all selected groups ({count})'
                values={{count: mergeableGroupCount}}
              />
            </Button>
          </div>
        )}
        {!!mergeResults.length && (
          <Alert variant='success'>
            {mergeResults.map((result, index) => (
              <div key={`${result}-${index}`}>{result}</div>
            ))}
            <strong>
              <FormattedMessage
                id='contacts.merge.remaining'
                defaultMessage='{count, plural, one {# candidate group remaining.} other {# candidate groups remaining.}}'
                values={{count: remainingGroups.length}}
              />
            </strong>
          </Alert>
        )}
        {!!mergeErrors.length && (
          <Alert variant='danger'>
            <strong>
              <FormattedMessage
                id='contacts.merge.someFailed'
                defaultMessage='Some groups could not be merged:'
              />
            </strong>
            {mergeErrors.map((result, index) => (
              <div key={`${result}-${index}`}>{result}</div>
            ))}
          </Alert>
        )}
        {!remainingGroups.length && (
          <Alert variant='success'>
            <FormattedMessage
              id='contacts.merge.noneRemain'
              defaultMessage='No pending duplicate groups remain.'
            />
          </Alert>
        )}
        {remainingGroups.map((group, groupIndex) => (
          <div className='border rounded mb-4' key={group.map((contact) => contact.id).join('-')}>
            <div className='d-flex justify-content-between align-items-center bg-light px-4 py-3'>
              <div>
                <strong>
                  <FormattedMessage
                    id='contacts.merge.groupTitle'
                    defaultMessage='Candidate group {n}'
                    values={{n: groupIndex + 1}}
                  />
                </strong>
                <span className='text-muted ms-2'>
                  <FormattedMessage
                    id='contacts.merge.groupHint'
                    defaultMessage='Select records to merge and which one to keep.'
                  />
                </span>
              </div>
              <Button
                size='sm'
                disabled={mergingAll || mergingGroup !== null || (selected[groupIndex]?.length || 0) < 2}
                onClick={() => void mergeGroup(groupIndex)}
              >
                {mergingGroup === groupIndex && <Spinner animation='border' size='sm' className='me-2' />}
                <FormattedMessage id='contacts.merge.mergeSelected' defaultMessage='Merge selected' />
              </Button>
            </div>
            <Table responsive hover className='mb-0 align-middle'>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id='contacts.merge.col.merge' defaultMessage='Merge' />
                  </th>
                  <th>
                    <FormattedMessage id='contacts.merge.col.keep' defaultMessage='Keep' />
                  </th>
                  <th>
                    <FormattedMessage id='common.field.contact' defaultMessage='Contact' />
                  </th>
                  <th>
                    <FormattedMessage
                      id='contacts.merge.col.phoneEmail'
                      defaultMessage='Phone / Email'
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id='common.field.dateOfBirth'
                      defaultMessage='Date of Birth'
                    />
                  </th>
                  <th>
                    <FormattedMessage id='contacts.merge.col.policies' defaultMessage='Policies' />
                  </th>
                  <th>
                    <FormattedMessage id='contacts.merge.col.reason' defaultMessage='Match reason' />
                  </th>
                </tr>
              </thead>
              <tbody>
                {group.map((contact) => {
                  const isSelected = (selected[groupIndex] || []).includes(contact.id)
                  return (
                    <tr key={contact.id}>
                      <td>
                        <Form.Check
                          checked={isSelected}
                          onChange={() => toggleContact(groupIndex, contact.id)}
                        />
                      </td>
                      <td>
                        <Form.Check
                          type='radio'
                          name={`primary-contact-${groupIndex}`}
                          checked={primary[groupIndex] === contact.id}
                          disabled={!isSelected}
                          onChange={() => setPrimary((current) => ({...current, [groupIndex]: contact.id}))}
                        />
                      </td>
                      <td>
                        <strong>{contact.fullName || contactLabel(contact.id)}</strong>
                        <div className='text-muted'>
                          <FormattedMessage
                            id='contacts.merge.idLabel'
                            defaultMessage='ID {id}'
                            values={{id: contact.id}}
                          />
                        </div>
                      </td>
                      <td>
                        <div>{contact.phone || EM_DASH}</div>
                        <div className='text-muted'>{contact.email || EM_DASH}</div>
                      </td>
                      <td>{contact.dateBirth || EM_DASH}</td>
                      <td>
                        <Badge
                          pill
                          style={{
                            backgroundColor: '#3e97ff',
                            color: '#ffffff',
                            minWidth: 28,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                          }}
                        >
                          {contact.policyCount}
                        </Badge>
                      </td>
                      <td>
                        {contact.reasons?.join(', ') ||
                          intl.formatMessage({
                            id: 'contacts.merge.manuallySelected',
                            defaultMessage: 'Manually selected',
                          })}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        ))}
        {!!selectedTotal && (
          <small className='text-muted'>
            <FormattedMessage
              id='contacts.merge.selectedTotal'
              defaultMessage='{count, plural, one {# contact record currently selected.} other {# contact records currently selected.}}'
              values={{count: selectedTotal}}
            />
          </small>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='secondary'
          onClick={onHide}
          disabled={mergingAll || mergingGroup !== null}
        >
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
